import React, { useState, useEffect, useContext, useRef } from "react";
import algoliasearch from "algoliasearch/lite";
import { SearchOptions, SearchResponse } from "@algolia/client-search";
import AlgoliaContext from "./AlgoliaContext";

const SHOW_LOADING_AFTER_MS = 300;

interface IAlgoliaHit {
  objectID: string;
}

interface ISearchState<T extends IAlgoliaHit = any> {
  initialSearchState: {
    index: string;
    context?: any;
  } & SearchOptions;
  initialResults?: SearchResponse<T> | null;
  onResults?: (args: {
    searchState: {
      index: string;
    } & SearchOptions;
    results: SearchResponse;
  }) => void;
}

export const useAlgoliaSearch = <T extends IAlgoliaHit = any>({
  initialSearchState,
  initialResults = null,
  onResults,
}: ISearchState<T>) => {
  if (!initialSearchState.index) {
    throw new Error("initialSearchState with index is required");
  }

  const componentIsMounted = useRef(true);
  const skip = useRef(!!initialResults);
  const algoliaContext = useContext(AlgoliaContext);
  const algoliaClient = algoliasearch(algoliaContext.appId, algoliaContext.apiKey);

  // The search query and options
  const [searchState, setSearchState] = useState({
    ...initialSearchState,
    context: initialSearchState.context || {},
    getRankingInfo: true,
  });

  const [results, setResults] = useState(initialResults);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    componentIsMounted.current = true;

    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { index, context, query, ...searchOptions } = searchState; // Algolia errors on unknown parameters so filter them out

    const getResults = async () => {
      if (!componentIsMounted.current) {
        return;
      }

      // Show loading after a while to prevent jumpyness
      const loadingTimeout = setTimeout(() => setLoading(true), SHOW_LOADING_AFTER_MS);

      // Get results
      try {
        const searchIndex = algoliaClient.initIndex(`${index}_${algoliaContext.environment}`);
        const res = await searchIndex.search<T>(query || "", searchOptions);

        if (!componentIsMounted.current) {
          clearTimeout(loadingTimeout);
          return;
        }

        setResults(res);

        clearTimeout(loadingTimeout);
        setLoading(false);

        if (onResults) {
          onResults({ searchState, results: res });
        }
      } catch (error) {
        if (error.message.toLowerCase().includes("unknown parameter")) {
          error.message = `${error.message} - if you want to add custom (non-Algolia) parameters, use searchState.context`;
        }

        // eslint-disable-next-line no-console
        console.error(`useAlgoliaSearch error: ${error.message}`);

        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    };

    if (skip.current) {
      skip.current = false;
      return;
    }

    getResults();
  }, [searchState]);

  return {
    searchState,
    setSearchState,
    results,
    loading,
  };
};

export const SearchContext = React.createContext(null);

/**
 * Hook to easily grab search props from context
 * Returns whatever is passed into SearchContext.Provider
 */
export const useSearchContext = () => {
  const searchContext = useContext(SearchContext);

  if (!searchContext)
    throw new Error("No search context found, make sure the component is wrapped with a <SearchContext.Provider>");
  return searchContext;
};
